// Boot (IMPLEMENTATION §2.1), M5:
// remoteLog → env → input hardening + audio arm + __gm → support probe → data (ErrorScreen on fail) → gate →
// SaveService.load | ?scenario | ?fresh / cold open → GameController (+ GameLoop) → Renderer (Phaser | Null)
// → viewport + layout → renderer init → UI → input → lifecycle (autosave, hidden) → splash off → ready.
// M6: input = PointerController (#input, pure gesture FSM) → InputRouter (commands, DOM ghost / bubbles,
// board glows, UI intents) + the §2.8 shop handoff (input/adopt.ts) + dev keys.
// M11: a saving instance claims the single-instance lock before it loads; after the input, the lifecycle
// installs every autosave trigger plus the lock / context-loss / bfcache / persist() / A2HS wiring.
// M12: the telemetry hub (persisted ring, SimEvent sink, fps / error rows; flushed once per save, after the
// game save), the trace recorder (GameController observers) and the lazy DebugPanel. The input router is the
// one source of `drag` / `rage_tap` rows (input/router.ts via debug/telemetryRows.ts + debug/rageTap.ts).
// M9: the unlock is armed before the data load and before any input listener (its capture listeners run
// first); after the data load `createAudio` (SfxEngine + event map) takes that handle and becomes the
// controller's audio sink — a command dispatched from an input handler plays its sound in that same gesture
// (iOS: the cold-open pop). Commands without an event note their buildings (audio/actions.ts), the router's
// refusal bonk and the lifecycle's hidden / Elsewhere / lock-lost mute (`audio.hold`) go to it as well.
// M7: the UI shell (ui/shell.ts controller + ui/App.tsx chrome) replaces the M0/M6 spike chrome: it is the
// router's UI sink, sees every snapshot (and the SimEvents it needs), runs the cold-open script, Sarge's
// lines, toasts and the hint finger per frame, and acts through ShellActions (below). Settings (meta.settings)
// drive the audio, the render quality (DPR 1.5 + 30 fps) and reduce motion (fx/motion.ts); the counter /
// Build Sell tool provide their DOM rects to the drop zones; the chrome registers the FX targets (fx/targets.ts,
// lead decision L16); the DebugPanel opens on 7 taps on the Settings version label (L14).
// ?spike=1 boots the M0 SpikeScene (7×9 layout) for the Phaser capability probe; the sim runs undrawn.

import { atlasOptions, type FrameDrawer } from './art/atlas.ts';
import { artEnv, atlasCatalog } from './art/catalog.ts';
import { createDomIcons } from './art/domIcons.ts';
import { drawFrame as drawArtFrame } from './art/generators.ts';
import { paletteHex } from './art/palette.ts';
import { actionBids } from './audio/actions.ts';
import { armAudio } from './audio/context.ts';
import { audioSettingsFrom, createAudio, type GameAudio } from './audio/index.ts';
import { type GameData, loadGameData, onDataReload } from './data/loader.ts';
import { createDebugPanel, openDebugOnTaps } from './debug/debugPanelEntry.ts';
import { loadEruda } from './debug/eruda.ts';
import { browserExportEnv, exportFile, fileStamp } from './debug/exportFile.ts';
import { createFpsOverlay, type FpsOverlay } from './debug/FpsOverlay.ts';
import type { GmDrop } from './debug/gmTypes.ts';
import { installRemoteLog } from './debug/remoteLog.ts';
import { saveEnvelopeText } from './debug/saveExport.ts';
import { TELEMETRY_KEY, type Telemetry } from './debug/telemetry.ts';
import { captureWindowErrors, createTelemetryHub } from './debug/telemetryHub.ts';
import { sessionInfoOf } from './debug/telemetryRows.ts';
import { type Deferred, deferred, installTestHooks } from './debug/testHooks.ts';
import { createTraceRecorder } from './debug/trace.ts';
import { hooksEnabled, parseEnv } from './env.ts';
import { createDomFx, type DomFx } from './fx/DomFx.ts';
import { createDragGhost } from './fx/DragGhost.ts';
import { createHintFinger } from './fx/HintFinger.ts';
import { reduceMotion, setReduceMotion } from './fx/motion.ts';
import { lowQuality, setLowQuality } from './fx/quality.ts';
import { createBubbles } from './fx/ReasonBubble.ts';
import { type FxTargetId, fxTarget, registerFxTarget } from './fx/targets.ts';
import { GameController } from './game/GameController.ts';
import {
  installLifecycle,
  installRobustness,
  type Lifecycle,
  type Robustness,
  saveGameThenTelemetry,
} from './game/lifecycle.ts';
import { loadScenario, type ScenarioRunner, startScenario } from './game/scenarios.ts';
import { SnapshotBuilder } from './game/snapshot.ts';
import { setAdoptTarget } from './input/adopt.ts';
import { createDropZones } from './input/dropZones.ts';
import { gestureCfg } from './input/gestureFsm.ts';
import { keyAction } from './input/keys.ts';
import { effectiveMode, MODE_START, modeCaps } from './input/modes.ts';
import { createPointerController } from './input/PointerController.ts';
import { namesOf, reasonText } from './input/preview.ts';
import { InputRouter } from './input/router.ts';
import {
  type Cell,
  cellCenter,
  clientToCell,
  computeLayout,
  type Layout,
  type Rect,
} from './layout/computeLayout.ts';
import { watchA2hs } from './platform/a2hs.ts';
import { deviceInfo } from './platform/device.ts';
import { watchContextLoss } from './platform/glContext.ts';
import { installInputHardening } from './platform/hardening.ts';
import {
  InstanceLock,
  instanceId,
  pickTransport,
  storageMarks,
  storedFingerprint,
  waitForStored,
} from './platform/instanceLock.ts';
import { requestPersistence } from './platform/storage.ts';
import { checkSupport } from './platform/support.ts';
import {
  createSafeAreaProbe,
  readViewport,
  type ViewportInfo,
  watchViewport,
} from './platform/viewport.ts';
import { fxConfig } from './render/fxConfig.ts';
import { geoCfg, juiceCfg } from './render/juice/config.ts';
import { ShotGate } from './render/juice/shotGate.ts';
import { createNullRenderer } from './render/NullRenderer.ts';
import { createPhaserRenderer, type PhaserRenderer } from './render/phaser/PhaserRenderer.ts';
import { SPIKE_COLS, SPIKE_ROWS } from './render/phaser/SpikeScene.ts';
import type { Renderer, SpikeReport } from './render/Renderer.ts';
import { defsHashOf, SAVE_KEYS, type SaveMeta, SaveService } from './save/SaveService.ts';
import { localStore } from './save/storage.ts';
import type { CmdResult, Command, RejectReason } from './sim/commands.ts';
import { formatNum } from './sim/core/num.ts';
import type { SimDefs } from './sim/defs.ts';
import { checkInvariants } from './sim/invariants.ts';
import { handAccepts, incomeView } from './sim/query.ts';
import { reconcileWithDefs } from './sim/reconcile.ts';
import { cloneState, coldOpenState } from './sim/setup.ts';
import type { SimState } from './sim/state.ts';
import { fillCopy } from './ui/copy.ts';
import type { ShellActions } from './ui/ctx.ts';
import { allChipTexts } from './ui/format.ts';
import { mountUi } from './ui/mount.tsx';
import { a2hsState, createOverlays, overlayState, showErrorScreen } from './ui/overlays/mount.tsx';
import { blockingSig } from './ui/overlays/store.ts';
import { wantsRotate } from './ui/panels.ts';
import { spikePanelSig, spikeReportSig } from './ui/SpikePanel.tsx';
import {
  applySettings,
  reduceMotionSig,
  type Settings,
  settingsFrom,
  settingsSig,
  systemReducedMotion,
} from './ui/settings.ts';
import { Shell, type UiFlag } from './ui/shell.ts';
import {
  copyTextSig,
  layoutSig,
  renderCounts,
  rotateSig,
  selectionSig,
  sheetSig,
} from './ui/state.ts';
import { configureHud, publishSnapshot } from './ui/store.ts';

// The index.html early-error guard stops once the module graph has evaluated this far.
window.__gmBooting = true;

const WALL_BG = '#2b2f3a';
/** The DOM targets coin flights / floaters fly to (L16) and the element each one resolves to. */
const FX_TARGETS: readonly (readonly [FxTargetId, string])[] = [
  ['hudCash', '[data-fx="hudCash"]'],
  ['hudRate', '[data-fx="hudRate"]'],
  ['counter', '[data-fx="counter"]'],
  ['goalChip', '[data-fx="goalChip"]'],
  ['mergeAll', '[data-fx="mergeAll"]'],
  ['shopTab', '[data-fx="shopTab"]'],
];
const FX_TARGET_IDS = FX_TARGETS.map(([id]) => id);
/** What the lock handover waits for: the old owner's last save AND its telemetry ring (M11 #1, M12 #12). */
const HANDOVER_KEYS = [SAVE_KEYS.main, TELEMETRY_KEY] as const;

function el(id: string): HTMLElement {
  const e = document.getElementById(id);
  if (!e) throw new Error(`#${id} missing from index.html`);
  return e;
}

/** A fresh player's seed (app side: the sim itself never reads a clock or Math.random). */
function randomSeed(): number {
  const a = new Uint32Array(1);
  crypto.getRandomValues(a);
  return a[0] ?? 0;
}

async function boot(ready: Deferred): Promise<void> {
  if (import.meta.env.DEV && import.meta.hot) installRemoteLog(import.meta.hot, window);
  const env = parseEnv(window.location.search, import.meta.env.DEV, import.meta.env.MODE);
  const hooks = hooksEnabled(env);

  // late-bound state read by __gm
  let data: GameData | null = null;
  let layout: Layout | null = null;
  let renderer: Renderer | null = null;
  let controller: GameController | null = null;
  let saves: SaveService | null = null;
  let telemetry: Telemetry | null = null;
  let lastTap: Cell | null = null;
  let lastDrop: GmDrop | null = null;
  let sfx: GameAudio | null = null;
  let playerApply: ((cmd: Command) => CmdResult) | null = null;
  let fps: FpsOverlay | null = null;
  let spikeRun: Promise<SpikeReport> | null = null;
  let router: InputRouter | null = null;
  let domFx: DomFx | null = null; // M10: coins, floaters, ribbons (#fx)
  let fxReceived = 0; // M10: events the DOM FX sink received (__gm.fanout().fx)
  let lock: InstanceLock | null = null;
  let life: Lifecycle | null = null;
  let robust: Robustness | null = null;
  let shell: Shell | null = null;
  /** The save's meta (settings, sessionCount, uiFlags): read by the UI shell and the saves. */
  let meta: SaveMeta = { settings: {}, sessionCount: 0, uiFlags: {} };
  const ghostRef: { visible(): boolean } = { visible: () => false };
  const needLayout = (): Layout => {
    if (!layout) throw new Error('layout not ready');
    return layout;
  };
  const needData = (): GameData => {
    if (!data) throw new Error('game data not loaded');
    return data;
  };
  const needGame = (): GameController => {
    if (!controller) throw new Error('game not started');
    return controller;
  };
  const needSaves = (): SaveService => {
    if (!saves) throw new Error('save service not ready');
    return saves;
  };
  const runSpike = (): Promise<SpikeReport> => {
    if (!renderer) return Promise.reject(new Error('renderer not ready'));
    if (!spikeRun) {
      spikeRun = renderer.spike().then((r) => {
        spikeReportSig.value = r;
        console.info(`[spike] ${JSON.stringify(r)}`);
        return r;
      });
    }
    return spikeRun;
  };

  // Input hardening, the audio unlock and __gm go in BEFORE the (DEV: networked) data load, as in M0: the
  // page is protected from the first touch, and a data failure is observable as `__gm.ready` rejecting.
  const inputRoot = el('input');
  installInputHardening(window, inputRoot);

  // M9 contract #1: the gesture unlock is armed here — before the data load and before the PointerController's
  // window-capture pointerup — so its capture listener runs first; a tap while the data loads creates the
  // context, and createAudio (below) attaches to it at once. The session type follows the saved Settings then.
  const audio = armAudio(window, { sessionType: 'ambient' });

  if (hooks) {
    installTestHooks(window, {
      ready: ready.promise,
      version: __APP_VERSION__,
      buildId: __BUILD_ID__,
      mode: import.meta.env.MODE,
      get renderer() {
        return env.render;
      },
      render: {
        highlighted: () => (renderer ? renderer.highlighted() : null),
        pixel: (x: number, y: number) =>
          renderer ? renderer.pixelAt(x, y) : Promise.resolve(null),
        count: (kind) => (renderer ? renderer.debug.count(kind) : 0),
        debugPos: (id) => (renderer ? renderer.debug.debugPos(id) : null),
        freezeAt: (ms) => {
          renderer?.debug.freezeAt(ms);
          domFx?.freezeAt(ms); // M10: the DOM FX freeze with the board
        },
        syncMs: () => (renderer ? renderer.debug.syncMs() : []),
        frames: () => (renderer ? renderer.debug.frames() : 0),
        fx: () => ({
          board:
            renderer && renderer.kind === 'phaser' ? (renderer as PhaserRenderer).fxStats() : null,
          dom: domFx ? domFx.stats() : null,
        }),
        fxReset: () => {
          if (renderer && renderer.kind === 'phaser') (renderer as PhaserRenderer).fxReset();
          domFx?.resetPeaks();
        },
        atlas: () => {
          const a =
            renderer && renderer.kind === 'phaser' ? (renderer as PhaserRenderer).atlas() : null;
          return a
            ? { pages: a.pages, frames: a.frames, bakeMs: a.bakeMs, pageSizes: a.pageSizes }
            : null;
        },
      },
      defs: () => needData().sim,
      state: () => cloneState(needGame().sim.state),
      hash: () => needGame().sim.hash(),
      apply: (cmd) => {
        if (!playerApply) throw new Error('game not started');
        return playerApply(cmd); // like the input's commands (M9 noteAction included)
      },
      advance: (n, opts) => needGame().advance(n, opts?.silent === true, opts?.publish !== false),
      events: () => needGame().drainGmEvents(),
      loadSim: (defs, state) => {
        needGame().replace(defs, cloneState(state));
        shell?.rebase(performance.now()); // a new game: no unlock toasts / banners for what it already had
      },
      load: (x) => {
        const r = needSaves().parse(x);
        if (!r.ok) return r;
        needGame().replace(needData().sim, r.loaded.state);
        shell?.rebase(performance.now());
        return { ok: true };
      },
      snapshot: () => needGame().publish(performance.now()),
      // M10: idle also waits for the DOM FX (coins, floaters, ribbons, Gallery floater windows)
      idle: async () => {
        await (renderer ? renderer.debug.idle() : Promise.resolve());
        if (!domFx?.busy()) return;
        await new Promise<void>((resolve) => {
          const check = (): void => {
            if (domFx?.busy()) requestAnimationFrame(check);
            else resolve();
          };
          check();
        });
        await (renderer ? renderer.debug.idle() : Promise.resolve());
      },
      setSpeed: (k) => needGame().setSpeed(k),
      fanout: () => ({
        render: renderer ? renderer.debug.eventsReceived() : 0,
        audio: sfx ? sfx.diag().received : 0, // what the audio facade itself received
        gm: needGame().fanoutCounts().gm,
        fx: fxReceived, // M10: what the DOM FX sink received
      }),
      telemetry: () => (telemetry ? telemetry.rows() : []),
      lifecycle: () => ({
        lock: lock ? lock.state : 'off',
        transport: lock ? lock.transportKind : 'off',
        saving: life ? life.saving : false,
        saves: life ? life.counts() : {},
        overlay: overlayState(),
        a2hs: a2hsState(),
        contextLost: robust ? robust.contextLost() : false,
        persist: robust ? robust.persist() : 'off',
        restores: robust ? robust.restores() : 0,
      }),
      lastTap: () => lastTap,
      lastDrop: () => lastDrop,
      layout: () => {
        const l = needLayout();
        return {
          cls: l.cls,
          cellCss: l.cellCss,
          boardRect: l.board,
          counterRect: l.counter,
          tabsRect: l.tabs,
          board: { x0: l.origin.x, y0: l.origin.y, cols: l.cols, rows: l.rows },
          cellCenter: (x: number, y: number) => cellCenter(l, x, y),
        };
      },
      spike: runSpike,
      audio: () => {
        // M9 contract #6: the facade's diag once it exists; before that (data loading) the bare unlock
        const d = sfx?.diag();
        return {
          state: d ? d.state : audio.state(),
          session: d ? d.session : audio.session(),
          armed: d ? d.armed : audio.armed(),
          beeps: d ? d.stats.played : 0,
          played: d ? d.stats.played : 0,
          recent: d ? d.recent : [],
          voices: d ? d.voices : [],
          held: audio.held(),
          ready: d ? d.ready : false,
          settings: d ? { ...d.settings } : null,
        };
      },
      drag: () => ({
        active: router ? router.dragging() : false,
        ghostVisible: ghostRef.visible(),
        sheet: sheetSig.value,
      }),
      input: () => {
        if (!router) throw new Error('input not ready');
        return router.diag();
      },
      perf: () => ({ fps: fps ? fps.fps() : 0, fpsLow: fps ? fps.low() : 0 }),
      ui: () => {
        if (!shell) throw new Error('UI shell not ready');
        return {
          ...shell.diag(),
          cls: needLayout().cls,
          hudRenders: renderCounts.hud,
          settings: { ...settingsSig.value },
          meta: JSON.parse(JSON.stringify(meta)) as SaveMeta,
          chipTexts: allChipTexts(shell.fmt()),
          fxTargets: Object.fromEntries(
            FX_TARGET_IDS.map((id) => {
              const b = fxTarget(id);
              return [id, b ? { x: b.x, y: b.y, w: b.width, h: b.height } : null];
            }),
          ),
        };
      },
    });
  }

  // §2.1: the support gate probes first (synchronous, before the data); its screen shows once the data
  // brought copy.json — the data is compiled into the production bundle, so nothing is downloaded for it.
  const support = checkSupport(env.render !== 'none');
  // data/defs before anything reads a tunable; a bad data file shows the ErrorScreen.
  let loaded: GameData;
  try {
    loaded = await loadGameData();
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    // no copy.json: the plain fallback strings (an unsupported browser still says why)
    showErrorScreen(
      support.ok ? 'Game data failed to load' : 'This browser is not supported',
      support.ok ? msg : `Missing: ${support.missing.join(', ')}. ${msg}`,
    );
    console.error('[boot] data', err);
    ready.reject(err);
    return;
  }
  data = loaded;
  bootCopy = loaded.copy;
  let ui = loaded.ui;

  if (!support.ok) {
    showErrorScreen(
      loaded.copy.errors.unsupportedTitle ?? '',
      fillCopy(loaded.copy.errors.unsupportedBody ?? '', { missing: support.missing.join(', ') }),
    );
    console.warn(`[boot] unsupported: ${support.missing.join(', ')}`);
    ready.reject(new Error(`unsupported browser: ${support.missing.join(', ')}`));
    return;
  }

  // ---- the game state: a scenario (hooks builds), the save, or a fresh cold open ----
  const defs: SimDefs = loaded.sim;
  let tick = (): number => 0;
  // window errors before the telemetry hub exists (the lock claim and its handover wait, below): buffered
  // and replayed into the ring once the hub is created (M12 captured them from here on)
  const earlyErrors: [source: string, message: string][] = [];
  const stopEarlyErrors = captureWindowErrors(window, (source, message) => {
    if (earlyErrors.length < loaded.ui.debug.telemetryErrorMax) earlyErrors.push([source, message]);
  });
  saves = new SaveService(localStore(window), {
    defs,
    defsHash: defsHashOf(defs),
    now: () => Date.now(),
    // the telemetry ring is created once the lock is held (below); SaveService logs only from its load on
    log: (row) => telemetry?.log(row),
    sanitizeLogMax: loaded.ui.save.sanitizeLogMax,
  });
  const seed = env.seed ?? (env.test ? 0 : randomSeed());
  // a ?scenario run never overwrites a real save — but outside hooks builds ?scenario is ignored (the save
  // loaded below), so that session saves like any other
  const autosave = env.autosave || (!env.test && (env.scenario === null || !hooks));
  // M11 single-instance lock (platform/instanceLock.ts): a SAVING instance claims the save before it loads —
  // the previous owner flushes its last save and yields — so it continues from the freshest state; the
  // newest instance wins. A non-saving run (?test, ?scenario) never claims and is never kicked out.
  let onLockLost: (wasOwner: boolean) => void = () => undefined;
  if (autosave) {
    lock = new InstanceLock({
      id: instanceId(crypto),
      transport: pickTransport(window),
      now: () => Date.now(),
      // storage marks: a claim stamp that outruns a clock set back, and the owner id every save checks
      marks: storageMarks(window),
      timers: window,
      handoverMs: loaded.ui.save.lockHandoverMs,
      onLost: (wasOwner) => onLockLost(wasOwner),
      // the yield names the save AND the telemetry ring the old owner left (its handover flush writes both;
      // another tab's writes arrive asynchronously): this tab loads — and reads the ring — only once its
      // storage shows both, so its first flush never overwrites the old owner's last rows
      handoverToken: () => storedFingerprint(window, HANDOVER_KEYS),
      awaitMain: (token) =>
        waitForStored(window, HANDOVER_KEYS, token, loaded.ui.save.lockHandoverMs).then((ok) => {
          if (!ok)
            console.warn('[lock] the handover save did not arrive in time; loading what is there');
        }),
    });
    await lock.claim();
  }
  // M12: the persisted telemetry ring + its SimEvent sink, fps and error rows (debug/telemetryHub.ts). It
  // reads the stored ring AFTER the lock claim: the previous owner's handover save flushed its rows (and its
  // session number) first, so this instance neither drops them at its next flush nor reuses the session.
  const hub = createTelemetryHub({
    cfg: loaded.ui.debug,
    hitchMs: loaded.ui.render.frameClampMs,
    store: localStore(window),
    now: () => Date.now(),
    tick: () => tick(),
  });
  stopEarlyErrors();
  hub.captureErrors(window);
  for (const [source, message] of earlyErrors) hub.error(source, message);
  telemetry = hub.telemetry;
  const tel = telemetry;
  const trace = createTraceRecorder(loaded.ui.debug.commandLogMax); // M12 trace export
  let start: SimState | null = null;
  let runner: ScenarioRunner | null = null;
  let loadedSave = false;
  /** A save was loaded: a returning session (the one-time "Workshop pauses" toast). */
  let returning = false;
  if (env.scenario !== null && hooks) {
    const sc = startScenario(defs, await loadScenario(env.scenario), env.seed);
    start = sc.state;
    runner = sc.runner;
  } else if (!env.fresh) {
    loadedSave = true;
    const r = saves.load();
    if (r.k === 'loaded') {
      start = r.state;
      meta = r.meta;
      returning = true;
      if (r.from === 'bak')
        console.warn(`[save] main unreadable (${r.corrupt}); loaded the backup`);
      if (r.mismatch) console.warn(`[save] other defs: refunded ${r.mismatch.refund}`);
    } else if (r.k === 'failed') {
      console.warn(`[save] no usable save: ${r.reason}`);
    }
  }
  const coldSeed = start === null ? seed : null; // M12 trace export
  if (start === null) start = coldOpenState(defs, seed);
  meta = { ...meta, sessionCount: meta.sessionCount + 1 };
  // a session that did not load (?fresh, a saving ?scenario) takes the main under the key as its
  // compare-and-swap baseline: it overwrites it deliberately, but only until another instance writes
  if (!loadedSave) saves.adopt();

  // ---- renderer ----
  const palette = paletteHex(loaded.palette.colors);
  // M10: FX run everywhere but ?test (zero-duration) — ?test&fx=1 runs them for the FX e2e
  const fx = fxConfig(loaded.juice, ui, palette, !env.fx);
  // M10 FX flags (L16 / L17): M7's Settings own them — reduce motion (System = prefers-reduced-motion) reaches
  // fx/motion.ts through reduceMotionSig, Low quality reaches fx/quality.ts through applyQuality (below). Camera
  // shake keeps its default (juice.json camera.shakeDefault: off in v0.1, no setting before N6) and never runs
  // with reduce motion.
  const shakeOn = loaded.juice.camera.shakeDefault;
  // one visible-shot decision per Gallery shot for the board FX and the DOM coins (UX §5 caps)
  const shots = new ShotGate({
    perSec: loaded.juice.caps.galleryShotsPerSec,
    totalPerSec: loaded.juice.caps.galleryShotsTotalPerSec,
  });
  const spikeMode = env.spike && env.render !== 'none';
  const r: Renderer =
    env.render === 'none'
      ? createNullRenderer()
      : createPhaserRenderer({
          parent: el('game'),
          background: WALL_BG,
          cellWorldUnits: ui.render.cellWorldUnits,
          frameCss: ui.layout.frame,
          mipmaps: env.mipmaps,
          mode: spikeMode ? 'spike' : 'factory',
          fx,
          juice: juiceCfg(loaded.juice, palette),
          geo: geoCfg(loaded.juice, ui, loaded.palette.art),
          motion: { reduceMotion, cameraShake: () => shakeOn && !reduceMotion(), lowQuality },
          shots,
          maxStepMs: ui.render.frameClampMs,
          syncSamples: ui.render.syncSamples,
          // M8: the real generators through the atlas path (the DOM icons draw the same catalog specs)
          bake: () => {
            const art = artEnv(loaded, vp.dprEff);
            const draw: FrameDrawer = (g, f, px) => drawArtFrame(g, f, px, art);
            return { ...atlasOptions(loaded, vp.dprEff), frames: atlasCatalog(loaded), draw };
          },
        });
  renderer = r;

  // ---- audio (M9): SfxEngine + event map on the unlock armed above; created BEFORE the controller (its
  // sink) and the input (contract #1). Settings: the saved ones (the M7 Settings screen), else the defaults.
  settingsSig.value = settingsFrom(meta.settings);
  const gameAudio = createAudio({
    data: loaded,
    handle: audio,
    settings: audioSettingsFrom(meta.settings),
  });
  sfx = gameAudio;

  // ---- the controller (loop + sim + fan-out + 10 Hz snapshot) ----
  configureHud(ui.hud.autoSmoothSec);
  const game = new GameController({
    defs,
    state: start,
    loop: {
      tickHz: defs.tickHz,
      frameClampMs: ui.render.frameClampMs,
      maxTicksPerFrame: ui.render.maxTicksPerFrame,
    },
    speed: env.test ? 0 : 1,
    snapshotHz: ui.render.snapshotHz,
    snapshots: new SnapshotBuilder(ui.goalChip),
    view: r,
    audio: gameAudio, // dispatch() fans out synchronously: an input handler's command sounds in its gesture
    // M10 DOM FX (created with the #fx layer below; late-bound)
    fx: {
      onEvents: (evs) => {
        fxReceived += evs.length; // __gm.fanout().fx (never on a silent advance)
        domFx?.onEvents(evs);
      },
      reset: () => domFx?.reset(),
    },
    // M11: economic events schedule the lifecycle's debounced save
    autosave: { onEvents: (evs) => life?.onEvents(evs) },
    // the HUD numbers (ui/store.ts) and the shell's per-snapshot work (unlock toasts, reveals, banners)
    publish: (snap, nowMs) => {
      publishSnapshot(snap, nowMs);
      shell?.onSnapshot(snap, nowMs);
    },
    gmEventsMax: hooks ? ui.render.gmEventBufferMax : 0,
    runner,
    onBoard: (_b, animate) => relayoutAll(animate),
    onError: (err) => {
      // the game halts: no more ticks, draws or saves (the last good save and its .bak stay intact)
      const msg = err instanceof Error ? err.message : String(err);
      tel.log({ event: 'error', subject: 'frame', value: 0, extra: msg });
      console.error('[game] halted', err);
      showErrorScreen(
        loaded.copy.errors.genericTitle ?? 'Something went wrong',
        msg,
        loaded.copy.errors.reload ?? null,
      );
    },
    logCap: ui.debug.commandLogMax,
    now: () => performance.now(),
    // M12 telemetry + trace; M7: the shell's toasts (machine merge, ghost credit) — late-bound
    observers: [hub.sink, trace, { onEvents: (evs) => shell?.observer.onEvents?.(evs, game.sim) }],
  });
  controller = game;
  const bootSim = game.sim;
  tick = () => game.sim.state.tick;
  // a player command: dispatch (its events reach the audio inside this task), then M9 contract #4 — commands
  // without an event of their own (pause, setTarget, paint / erase chutes) note the buildings they touched
  const playerDispatch = (cmd: Command): CmdResult => {
    const res = game.dispatch(cmd);
    for (const bid of actionBids(cmd, res, game.sim.state) ?? []) gameAudio.noteAction(bid);
    return res;
  };
  playerApply = playerDispatch;
  r.bind({ state: () => game.sim.state, defs: () => game.sim.defs });

  // ---- viewport + layout: the board is the expansion mask (7×9 with the spike scene) ----
  const probe = createSafeAreaProbe();
  // §2.9: dprEff = min(dpr, 2); Low quality (?quality=low or Settings → Low quality) uses 1.5 and 30 fps.
  const lowQualityOn = (): boolean => env.quality === 'low' || settingsSig.value.lowQuality;
  let dprCap = lowQualityOn() ? ui.render.dprCapLow : ui.render.dprCap;
  let vp: ViewportInfo = readViewport(probe, dprCap);
  const layoutFor = (v: ViewportInfo): Layout => {
    const s = game.sim.state;
    const m = game.sim.defs.board.expansions[s.bounds];
    const board =
      spikeMode || !m
        ? { cols: SPIKE_COLS, rows: SPIKE_ROWS, origin: { x: 0, y: 0 } }
        : { cols: m.x1 - m.x0 + 1, rows: m.y1 - m.y0 + 1, origin: { x: m.x0, y: m.y0 } };
    return computeLayout({ vw: v.vw, vh: v.vh, insets: v.insets, ...board }, ui.layout);
  };
  layout = layoutFor(vp);
  const device = deviceInfo(window); // standalone (session_start) and the A2HS card
  hub.sessionStart(
    sessionInfoOf(window, vp, layout, env.render, __APP_VERSION__, __BUILD_ID__, device.standalone),
  );
  const zones = createDropZones();
  zones.refresh(layout, ui.shop.peek);
  layoutSig.value = layout;

  // Installed BEFORE the (100–300+ ms) renderer init so a rotation / iOS standalone viewport correction
  // during boot is never lost. relayout() before init only records the size; init applies the latest.
  function relayoutAll(animate = false): void {
    vp = readViewport(probe, dprCap);
    const l = layoutFor(vp);
    layout = l;
    hub.layout(l); // M12: a `layout` telemetry row when the class / cell size changed
    r.relayout(l, vp, animate);
    zones.refresh(l, ui.shop.peek);
    router?.relayout();
    layoutSig.value = l;
    // UX §1.6: a phone held in landscape gets the RotateOverlay (input blocked, the sim keeps running)
    rotateSig.value = wantsRotate(vp.vw, vp.vh, ui.overlay.rotateMaxHs)
      ? (data?.copy.overlay.rotate ?? '')
      : null;
  }
  watchViewport(() => relayoutAll(false), ui.layout.resizeDebounceMs);
  // DEV: saving shared/data/*.json recompiles (data/loaderDev.ts): new defs, the same game reconciled with
  // them like a save written with other defs (unknown ids dropped with a refund), relayout. Saves from then on
  // carry the new defsHash. A state the new defs cannot hold keeps the old defs (and says so).
  onDataReload((d) => {
    const next = cloneState(game.sim.state);
    let bad: string[];
    try {
      const rep = reconcileWithDefs(next, d.sim);
      if (rep.dropped.length > 0) console.warn(`[data] reload reconciled: refunded ${rep.refund}`);
      bad = checkInvariants(next, d.sim);
    } catch (err) {
      bad = [err instanceof Error ? err.message : String(err)];
    }
    if (bad.length > 0) {
      console.warn(`[data] reload kept the old defs: the game state does not fit them (${bad[0]})`);
      return;
    }
    data = d;
    ui = d.ui;
    needSaves().setDefs(d.sim, defsHashOf(d.sim));
    game.replace(d.sim, next);
    relayoutAll(false);
  });

  if (env.bootDelayMs > 0) await new Promise((res) => setTimeout(res, env.bootDelayMs)); // ?test only
  if (lowQualityOn()) r.setFpsLimit(ui.render.fpsLow);
  await r.init(layout, vp);
  // Belt and braces: re-read once after init (a resize inside the debounce window is not lost either).
  relayoutAll(false);
  game.publish(performance.now());
  r.drive((now) => {
    hub.frame(now); // M12: fps rows (wall-time frame intervals)
    game.frame(now);
    router?.frame(now);
    shell?.frame(now); // M7: cold-open script, Sarge, toasts, hint finger
  });

  // ---- input (M6): #input → PointerController (pure gesture FSM) → InputRouter → commands / FX / UI ----
  const fxRoot = el('fx');
  // M8: DOM icons from the generators (the M6 IconProvider contract: drawn into the caller's canvas)
  const icons = createDomIcons({
    defs: needData, // the current GameData (a DEV data reload swaps it: the icon cache rebuilds)
    dpr: () => vp.dprEff,
  });
  const ghost = createDragGhost(fxRoot, {
    icons,
    overshoot: loaded.juice.easing.popOvershoot,
    now: () => performance.now(),
    raf: (cb) => requestAnimationFrame(cb),
    cancelRaf: (id) => cancelAnimationFrame(id),
  });
  ghostRef.visible = ghost.visible;
  // M10: coin flights, "+$X" floaters, NEW! ribbons (fx/DomFx.ts; targets via fx/targets.ts, L16)
  domFx = createDomFx({
    root: fxRoot,
    juice: loaded.juice,
    colors: {
      text: palette['ui.cream'] ?? '#f4ecd8',
      ink: palette['ui.ink'] ?? '#342117',
      ribbon: palette['glow.merge'] ?? '#fedc54',
      label: palette['ui.cream'] ?? '#f4ecd8',
      coin: palette['ui.money'] ?? '#f4c947',
    },
    copy: {
      floater: loaded.copy.drag.sell ?? '+{value}',
      newTag: loaded.copy.tag.new ?? 'NEW!',
      level: loaded.copy.tag.level ?? '{name} L{level}',
    },
    instant: fx.instant,
    layout: () => layout,
    state: () => game.sim.state,
    defs: () => game.sim.defs,
    incomePerSec: () => incomeView(game.sim.state, game.sim.defs).totalPerSec,
    money: (v) =>
      fillCopy(loaded.copy.hud.money ?? '{n}', { n: formatNum(Math.floor(v), ui.numberFormat) }),
    fill: fillCopy,
    coinImage: (px) => {
      const c = document.createElement('canvas');
      if (!icons.drawFrame('ui.coin', c, px)) return null;
      const url = c.toDataURL('image/png');
      c.width = 0; // free the scratch canvas (iOS canvas memory, §2.9)
      c.height = 0;
      return url;
    },
    reduceMotion,
    shots,
    now: () => performance.now(),
    raf: (cb) => requestAnimationFrame(cb),
    cancelRaf: (id) => cancelAnimationFrame(id),
    maxStepMs: ui.render.frameClampMs,
  });
  const bubbles = createBubbles(fxRoot, {
    fontPx: ui.input.bubblePx,
    iconPx: ui.input.bubbleIconPx,
    icons,
    gapPx: ui.input.bubbleGapPx,
    ink: palette['ui.ink'] ?? '#1d1f26',
    paper: palette['ui.cream'] ?? '#f4ecd8',
    bad: palette['glow.invalid'] ?? '#d73337',
  });
  // M6 ships the finger; the M7 shell decides which hint it shows (tutorialView → ui/hints.ts)
  const finger = createHintFinger(fxRoot, {
    loops: ui.hints.fingerLoops,
    idleAlpha: ui.hints.fingerIdleAlpha,
    dragMs: ui.hints.fingerDragMs,
    pauseMs: ui.hints.fingerPauseMs,
    pressScale: loaded.juice.events.hit.squash,
    sizePx: () => needLayout().cellCss,
  });
  const money = (n: number): string =>
    fillCopy(loaded.copy.hud.money ?? '{n}', { n: formatNum(Math.floor(n), ui.numberFormat) });
  /** Client rect of the first element matching `sel` (null when it is not rendered). */
  const domRect = (sel: string): Rect | null => {
    const e = document.querySelector(sel);
    if (!e) return null;
    const b = e.getBoundingClientRect();
    return b.width > 0 && b.height > 0 ? { x: b.x, y: b.y, w: b.width, h: b.height } : null;
  };

  // ---- the UI shell (M7): the router's UI sink, per-frame script, snapshot / event reactions ----
  const theShell = new Shell({
    copy: () => needData().copy,
    ui: () => needData().ui,
    defs: () => game.sim.defs,
    state: () => game.sim.state,
    mode: () =>
      router ? router.mode() : effectiveMode(MODE_START, modeCaps(game.sim.state, game.sim.defs)),
    now: () => performance.now(),
    finger,
    rect: (id) => {
      switch (id) {
        case 'counter':
          return domRect('[data-zone="counter"]');
        case 'shopTab':
          return domRect('[data-tab="shop"]');
        case 'buildTab':
          return domRect('[data-tab="build"]');
        case 'chuteTool':
          return domRect('[data-tool="chute"]');
        case 'leadCard':
          return domRect('[data-item="deposit.lead"]');
        case 'placeBtn':
          return domRect('[data-testid="pop-place"]');
        case 'sarge':
          return domRect('.sarge-bubble');
      }
    },
    flags: {
      has: (f: UiFlag) => meta.uiFlags[f] === 1,
      set: (f: UiFlag) => {
        meta = { ...meta, uiFlags: { ...meta.uiFlags, [f]: 1 } }; // saved with the next save
      },
    },
    howto: env.howto,
  });
  shell = theShell;
  const inputRouter = new InputRouter({
    game: {
      state: () => game.sim.state,
      defs: () => game.sim.defs,
      dispatch: playerDispatch,
    },
    layout: needLayout,
    zones,
    cfg: {
      input: ui.input,
      snapBackMs: fx.instant ? 0 : ui.input.snapBackMs,
      peekPx: ui.shop.peek,
      liftScale: loaded.juice.events.lift.scale,
      refreshMs: 1000 / ui.render.snapshotHz,
    },
    copy: loaded.copy,
    money,
    ghost,
    bubble: bubbles,
    view: r,
    ui: theShell.inputUi,
    telemetry: tel,
    // ui.bonk through the event map's shared bonk limiter (a dispatched drop's reject event may have bonked)
    sfx: () => void gameAudio.bonk(),
    now: () => performance.now(),
    onTap: (cell) => {
      lastTap = cell;
    },
    onDrop: (d) => {
      lastDrop = d;
    },
  });
  router = inputRouter;
  const pointer = createPointerController({
    root: inputRoot,
    win: window,
    cfg: gestureCfg(ui.input),
    now: () => performance.now(),
    classify: (x, y, mouse) => inputRouter.classify(x, y, mouse),
    cellAt: (x, y) => clientToCell(needLayout(), x, y),
    onGesture: (out, meta) => inputRouter.gesture(out, meta),
  });
  inputRouter.attach(pointer);
  setAdoptTarget(inputRouter);
  // a blocking overlay (context lost, Elsewhere / lock lost, the ErrorScreen of a halted game) and the
  // RotateOverlay end the active gesture: its drag / hold / paint must not dispatch behind it (a snap back)
  blockingSig.subscribe((o) => {
    if (o.k !== 'none') pointer.cancel('overlay');
  });
  rotateSig.subscribe((t) => {
    if (t !== null) pointer.cancel('overlay');
  });
  // the finger's idle clock; a touch on the board closes a card / the goal card / a pop-over (non-modal:
  // the touch still acts), a touch on the chrome only counts as input
  inputRoot.addEventListener('pointerdown', () => theShell.noteInput(true), true);
  window.addEventListener('pointerdown', () => theShell.noteInput(false), true);
  // desktop dev keys (§2.7 "Mouse"): B build · S shop · M Merge All · Ctrl/Cmd+Z undo · Esc — never behind a
  // blocking overlay or on a halted game (an instance that lost the save, a sim whose frame threw)
  window.addEventListener('keydown', (e) => {
    if (overlayState() !== 'none' || game.halted) return;
    const t = e.target as HTMLElement | null;
    const a = keyAction({
      key: e.key,
      ctrlKey: e.ctrlKey,
      metaKey: e.metaKey,
      altKey: e.altKey,
      shiftKey: e.shiftKey,
      repeat: e.repeat,
      editable:
        t !== null && (t.isContentEditable || t.tagName === 'INPUT' || t.tagName === 'TEXTAREA'),
    });
    if (a === null) return;
    e.preventDefault();
    inputRouter.key(a);
  });

  // M7 drop zones: the counter and the Build Sell tool are real DOM elements (their rects, re-read on
  // relayout, mode switch and drag start); before they render, the layout's rects stand in
  zones.provide('counter', () => domRect('[data-zone="counter"]') ?? needLayout().counter);
  zones.provide('sell', () => domRect('[data-zone="sell"]'));
  // L16: coin flights / floaters (M10) fly to these DOM targets
  for (const [id, sel] of FX_TARGETS) {
    registerFxTarget(id, () => {
      const e = document.querySelector(sel);
      if (!e) return null;
      const b = e.getBoundingClientRect();
      return b.width > 0 && b.height > 0 ? b : null;
    });
  }

  // Settings → audio, render quality (+ M10's fewer particles, fx/quality.ts), reduce motion (fx/motion.ts, L16)
  // and meta.settings
  const applyQuality = (low: boolean): void => {
    const on = low || env.quality === 'low';
    setLowQuality(on);
    r.setFpsLimit(on ? ui.render.fpsLow : 0);
    const cap = on ? ui.render.dprCapLow : ui.render.dprCap;
    if (cap !== dprCap) {
      dprCap = cap;
      relayoutAll(false);
    }
  };
  const settingsSinks = {
    audio: gameAudio,
    quality: applyQuality,
    persist: (record: Record<string, unknown>) => {
      meta = { ...meta, settings: record }; // saved with the next save
    },
  };
  let applied: Settings = settingsSig.value;
  applySettings(null, applied, settingsSinks);
  const rmQuery =
    typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : null;
  if (rmQuery) {
    systemReducedMotion.value = rmQuery.matches;
    rmQuery.addEventListener?.('change', (e) => {
      systemReducedMotion.value = e.matches;
    });
  }
  reduceMotionSig.subscribe((v) => {
    setReduceMotion(v);
    document.documentElement.dataset.rm = v ? '1' : '0';
  });

  const refuseText = (reason: RejectReason, bid: number | null): string =>
    reasonText(
      reason,
      {
        copy: needData().copy,
        names: namesOf(game.sim.defs),
        money,
        s: game.sim.state,
        accepts: (b) => handAccepts(game.sim.state, game.sim.defs, b),
      },
      bid,
    );
  const actions: ShellActions = {
    // a UI button's command: the chrome shows its result at once (a fresh snapshot), not up to 100 ms later
    dispatch: (cmd) => {
      const res = playerDispatch(cmd);
      game.publish(performance.now());
      return res;
    },
    refuse(reason, at, bid = null) {
      if (reason === 'cooldown') return;
      bubbles.reason(refuseText(reason, bid), at.x, at.y, ui.input.reasonBubbleMs);
      gameAudio.bonk();
    },
    toggleBuild: () => inputRouter.toggleBuild(),
    setTool: (t) => inputRouter.setTool(t),
    undoBuild: () => void playerDispatch({ c: 'undoBuild' }),
    tapZone: (z) => inputRouter.tapZone(z),
    selected: () => selectionSig.value !== null,
    beginTapPlace: (item) => inputRouter.beginTapPlace(item),
    settings(next) {
      applySettings(applied, next, settingsSinks);
      applied = next;
    },
    exportTelemetry: () =>
      exportFile(
        browserExportEnv(window, (f) => {
          copyTextSig.value = f.text;
        }),
        {
          name: `gunmerger-telemetry-${fileStamp(new Date())}.csv`,
          type: 'text/csv',
          text: tel.csv(),
        },
      ),
    resetSave() {
      // Settings → Reset save (held): the save and its backup go and a fresh cold open starts; the settings,
      // the session count and this device's one-time A2HS flag stay
      const sv = needSaves();
      if (!sv.writable) return;
      pointer.cancel('mode');
      inputRouter.clearSelection();
      sv.reset();
      meta = { ...meta, uiFlags: meta.uiFlags.a2hs === 1 ? { a2hs: 1 } : {} };
      const seed2 = env.seed ?? (env.test ? 0 : randomSeed());
      game.replace(game.sim.defs, coldOpenState(game.sim.defs, seed2));
      relayoutAll(false);
      theShell.rebase(performance.now());
    },
    click: () => void gameAudio.play('ui.click'),
  };
  mountUi(el('ui'), {
    shell: theShell,
    actions,
    icons,
    data: needData,
    state: () => game.sim.state,
    layout: () => layout,
    instant: fx.instant,
    version: __APP_VERSION__,
    buildId: __BUILD_ID__,
  });
  if (returning) theShell.onReturn();

  // M11 (game/lifecycle.ts): saves every ui.json save.autosaveSec, 1 s after buy / build / expand / sell,
  // on hidden, pagehide, freeze and context loss; the lock, context-loss, bfcache and persist() wiring;
  // the overlays (ui/overlays) and the one-time Add-to-Home-Screen card (platform/a2hs.ts).
  const ov = createOverlays(loaded.copy);
  const lc = installLifecycle({
    win: window,
    autosaveMs: autosave ? (env.autosaveMs ?? ui.save.autosaveSec * 1000) : null,
    debounceMs: ui.save.debounceSec * 1000,
    // hidden / pagehide: abort the active M6 gesture FIRST (the router ends its drag / hold / paint: snap-back,
    // its own release / holdEnd), then the controller's release + holdEnd (no-ops by then) — the pointer's own
    // visibilitychange listener does the same, but a pagehide can arrive without a hidden before it
    onHidden: () => {
      pointer.cancel('hidden');
      game.onHidden();
    },
    onShown: () => {
      game.onShown();
      theShell.onReturn(); // v0.1: the one-time "Workshop pauses while you're away" toast
    },
    onResume: () => robust?.onResume(),
    onRestore: () => robust?.onRestore(),
    // never while a (bfcache re-)claim is pending, and never once the lock's storage mark names another
    // instance (a frozen / suspended owner that missed the newer claim): installRobustness.mayWrite
    canSave: () => (robust ? robust.mayWrite() : lock === null || lock.state !== 'claiming'),
    // hidden / pagehide / ElsewhereOverlay / lock lost: the lifecycle hold (not the Settings mute); while held
    // the unlock resumes the context neither on a gesture nor on `visible`
    audio: { setMuted: (muted) => gameAudio.hold(muted) },
    // the game save, then at most one telemetry write (game/lifecycle.ts saveGameThenTelemetry: none on a
    // conflict / disabled service; a failed write frees the stored ring for one retry)
    save: (reason) =>
      saveGameThenTelemetry(
        {
          halted: () => game.halted, // never save the state a failed frame left behind
          write: () => needSaves().save(game.sim.state, meta),
          telemetry: hub,
          onConflict: () => robust?.onConflict(),
          warn: (msg) => console.warn(msg),
        },
        reason,
      ),
  });
  life = lc;
  robust = installRobustness({
    lock,
    life: lc,
    saves: needSaves(),
    game,
    overlays: ov,
    canvas: r.canvas(),
    bindLockLost: (fn) => {
      onLockLost = fn;
    },
    watchContextLoss,
    persist: () => requestPersistence(window),
    telemetry: tel,
    reload: () => window.location.reload(),
  });
  const a2hs = watchA2hs({
    win: window,
    enabled: env.a2hs,
    iosSafari: device.iosSafari,
    standalone: device.standalone,
    afterMs: ui.lifecycle.a2hsAfterSec * 1000,
    flagged: () => meta.uiFlags.a2hs === 1,
    note: () => needLayout().note,
    now: () => performance.now(),
    show: (kind) => {
      meta = { ...meta, uiFlags: { ...meta.uiFlags, a2hs: 1 } }; // one-time: saved with the next save
      ov.a2hs(kind, () => undefined);
    },
  });
  a2hs.check();

  // M12 DebugPanel: a lazy chunk, opened by ?debug or 7 quick taps on the Settings version label — in every
  // build, production included (lead decision L14; M12 decision 6).
  const debugPanel = createDebugPanel({
    win: window,
    game: needGame,
    layout: () => layout,
    lastTap: () => lastTap,
    telemetry: tel,
    trace,
    saveText: () => saveEnvelopeText(game.sim.defs, game.sim.state, meta, Date.now()),
    seed: () => (game.sim === bootSim ? coldSeed : null),
    dev: env.dev,
    version: __APP_VERSION__,
    buildId: __BUILD_ID__,
    cfg: ui.debug,
    refreshHz: ui.render.snapshotHz,
  });
  openDebugOnTaps(
    window,
    {
      el: () => document.querySelector<HTMLElement>('[data-testid="version-label"]'),
      taps: ui.debug.versionTapsForPanel,
      gapMs: ui.debug.versionTapGapMs,
    },
    () => void debugPanel.open(),
  );
  if (env.debug) void debugPanel.open();
  if (env.debug) void loadEruda();
  if (env.debug || env.fps || env.dev || env.spike)
    fps = createFpsOverlay(document, {
      hz: ui.debug.fpsOverlayHz,
      good: ui.debug.fpsGood,
      warn: ui.debug.fpsWarn,
    });

  const splash = el('splash');
  splash.classList.add('gone');

  if (env.spike) {
    spikePanelSig.value = true;
    await runSpike();
  }
  ready.resolve();
}

/** copy.json once loaded (the boot-failure screen falls back to English before that). */
let bootCopy: GameData['copy'] | null = null;

const bootReady = deferred();
boot(bootReady).catch((err: unknown) => {
  const msg = err instanceof Error ? err.message : String(err);
  showErrorScreen(
    bootCopy?.errors.genericTitle ?? 'Something went wrong',
    msg,
    bootCopy?.errors.reload ?? null,
  );
  console.error('[boot] failed', err);
  bootReady.reject(err);
});
